import React, { useEffect, useRef, useState, useMemo } from "react";
import {
  useI18N,
  usePostAddMapperNotes,
  VenueDetailsBySlugResponse,
  useZoomDetection,
  useModalHeight,
} from "common/hooks";

import Loader from "@/components/common/Loader";
import { useCustomToast } from "@/hooks/useCustomToast";
import { XIcon } from "lucide-react";
import { Textarea } from "@/shadcn/components/ui/textarea";
import WideButton from "@/components/common/WideButton";
import { useDarkMode } from "@/hooks/useDarkMode";
import { createPageAnalytics, trackEvent } from "common/analytics";
import SelectDropDown from "@/components/common/SelectDropDown";
import MultipleSelectDropDown from "@/components/common/MultipleSelectDropDown";
import FloatingLabelInput from "@/components/InputBoxes/TextInput";
import { cn } from "@/lib/utils";
import { StandardButton } from "@/components/common/StandardButton";
import RadioButton from "@/components/common/RadioButton";

function AddMapperNote(props: {
  venueDetailsResponse: VenueDetailsBySlugResponse;
  onClose: () => void;
  onInfoClick: () => void;
}) {
  const i18n = useI18N();
  const { modalStyle, modalClassName } = useModalHeight();
  const showToast = useCustomToast();

  const [notes, setNotes] = useState("");
  const { data: mutateResult, error, status, mutate } = usePostAddMapperNotes();
  const [selectedOption, setSelectedOption] = useState(0);
  const noteRef = useRef(null);
  const isDark = useDarkMode();
  const [returnOnDate, setReturnOnDate] = useState("");
  const [actionOnDate, setActionOnDate] = useState("");
  const [selectedAreas, setSelectedAreas] = useState<number[]>([]);
  const [applyToWholeVenue, setApplyToWholeVenue] = useState(false);

  // Zoom awareness state and logic
  const isZoomedIn = useZoomDetection();

  const selectedAreaDetails = useMemo(() => {
    if (selectedAreas.length > 0) {
      const sections =
        props.venueDetailsResponse.details.businessVenueNotesAreaLocations.filter(
          (e) => selectedAreas.includes(Number(e.id)),
        );
      return sections.map((section) => ({
        parentId: Number(section.id),
        parentName: section.name,
        location_type: section.location_type,
      }));
    }
    return [];
  }, [
    selectedAreas,
    props.venueDetailsResponse.details.businessVenueNotesAreaLocations,
  ]);

  const selectedAction = useMemo(() => {
    return props.venueDetailsResponse.details.businessNotesActions?.find(
      (action) => action.id === selectedOption,
    );
  }, [selectedOption, props.venueDetailsResponse.details.businessNotesActions]);

  const isNoteTextMandatory = useMemo(() => {
    // Check if the selected action has children and look for isNoteTextMandatory in the children
    if (selectedAction?.children_business_notes_action?.length > 0) {
      const childAction = selectedAction.children_business_notes_action.find(
        (child) => child.id === selectedOption,
      );
      return childAction?.is_note_text_mandatory === 1;
    }
    // If no children, check if the action itself has the property (fallback)
    return (selectedAction as any)?.isNoteTextMandatory === 1;
  }, [selectedAction, selectedOption]);

  // page_view_page -> add_mapper_note_form_opened. Mounted only while the form is shown -> mount == opened.
  useEffect(() => {
    createPageAnalytics("add_mapper_note_form").track("page_view_page", {
      brand_id: props.venueDetailsResponse.details?.brandId ?? "",
      brand_name: props.venueDetailsResponse.details?.brandName ?? "",
      business_id:
        props.venueDetailsResponse.details?.business?.id?.toString() ?? "",
      mapper_note_type: "mapper_note",
      place_name: props.venueDetailsResponse.details.name ?? "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (error) {
      showToast(error.message, "error");
    }
  }, [error]);

  useEffect(() => {
    if (mutateResult) {
      // submit_action_button -> alert_added (outcome, on success). A mapper note isn't an alert,
      // so alert_* props use neutral mappings (per the agreed convention): note-action title for
      // title/type label, selected area (or "Whole venue") for location. photos_count = the venue's
      // total photo count.
      const d = props.venueDetailsResponse.details;
      trackEvent("alert_added", {
        alert_location_name: applyToWholeVenue
          ? "Whole venue"
          : selectedAreaDetails[0]?.parentName ?? "",
        alert_title: selectedAction?.title ?? "",
        alert_type: "mapper_note",
        alerts_added_count: 1,
        alerts_count:
          ((d as { businessNotesAll?: unknown[] }).businessNotesAll?.length ??
            0) + 1,
        business_id: d?.business?.id?.toString() ?? "",
        mapper_note_type: selectedAction?.title ?? "mapper_note",
        photos_count: d.businessPhotos?.length ?? 0,
        place_name: d.name ?? "",
      });

      if (selectedOption === 1 || selectedOption === 6) {
        const urlObj =
          props.venueDetailsResponse.details.businessNotesActions?.filter(
            (e) => e.id === selectedOption,
          );
        if (urlObj.length > 0) {
          // console.log("urlObj", urlObj);
          // Open the first URL in a new tab
          window.open((urlObj[0] as any).external_link, "_blank"); // Assuming urlObj[0] has a'url' property
        }
      }
      console.log("mutateResult", mutateResult);
      showToast(i18n.get("mapperNoteAdded"), "success");
      setNotes("");
      setSelectedOption(0);
      setSelectedAreas([]);
      props.onClose();
    }
  }, [mutateResult]);

  const submit = () => {
    //refactor this
    const locationDetails = [];

    if (applyToWholeVenue) {
      locationDetails.push({
        location_type: "Business",
        location_id:
          props.venueDetailsResponse.details?.business?.id.toString() || "",
      });
    } else {
      // Add all selected areas to locationDetails array
      selectedAreaDetails.forEach((area) => {
        locationDetails.push({
          location_type: area.location_type,
          location_id: area.parentId.toString(),
        });
      });
    }

    mutate({
      venueId:
        props.venueDetailsResponse.details?.business?.id.toString() || "",
      note: notes,
      businessReportIssueAction: selectedOption.toString(),
      returnOnDate: returnOnDate,
      actionOnDate: actionOnDate,
      locationDetails: locationDetails,
    });
  };

  const handleOptionPress = (id: number) => {
    console.log("handleOptionPress", id);
    setSelectedOption(selectedOption === id ? 0 : id);

    console.log("handleOptionPress selectedOption", selectedOption);

    // Reset Return on Date if option is not 8
    if (id !== 8) {
      setReturnOnDate("");
    }

    // Reset Return on Date if option is not 8
    if (id !== 15) {
      setActionOnDate("");
    }

    // Reset selected areas if not"Incomplete venue"
    if (id !== 4) {
      // 4 is the ID for"Incomplete venue"
      setSelectedAreas([]);
    }
  };

  const isSubmitDisabled = () => {
    // Disable if option 8 is selected but no return date is set
    if (selectedOption === 8 && !returnOnDate) return true;

    // Disable if option 15 is selected but no action date is set
    // if (selectedOption === 15 && !actionOnDate) return true;

    // Disable if"Incomplete venue" is selected but no area is selected
    if (
      selectedOption === 4 &&
      !applyToWholeVenue &&
      selectedAreas.length === 0
    )
      return true;

    // Additional conditions for other options
    const basicValidation = selectedOption === 0;

    // Only require note text if isNoteTextMandatory is true
    if (isNoteTextMandatory && notes.length < 5) {
      return true;
    }

    return basicValidation;
  };

  return (
    <>
      <section style={modalStyle} className={cn(modalClassName)} role="none">
        {status === "pending" && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex justify-center items-start pt-[250px] z-50">
            <Loader />
          </div>
        )}

        <header
          className={`py-4 min-h-14 flex items-center justify-between text-center px-6 border-b border-border-token-brand-minimal bg-bg-brand-secondary-default ${!isZoomedIn ? "sticky top-0 z-10" : "relative z-10"}`}
        >
          <StandardButton
            onClick={() => {
              createPageAnalytics("add_mapper_note_form").track("close_action_button", {
                brand_id: props.venueDetailsResponse.details?.brandId ?? "",
                brand_name: props.venueDetailsResponse.details?.brandName ?? "",
                business_id:
                  props.venueDetailsResponse.details?.business?.id?.toString() ?? "",
                mapper_note_type: "mapper_note",
                place_name: props.venueDetailsResponse.details.name ?? "",
              });
              props.onClose();
            }}
            variant="closeModal"
            icon={<XIcon color={isDark ? "#A39FE8" : "#352FC3"} />}
            aria-label="Close modal"
            tabIndex={0}
            className="flex-shrink-0"
          />
          <h1 className="title-s-semibold text-center text-fg-neutral-strong flex-1 px-4">
            {i18n.get("createNote")}
          </h1>
          <div className="flex-shrink-0 w-10"></div>
          {" "}
          {/* Spacer to balance the layout */}
        </header>

        <div
          aria-label="Modal Content"
          className="flex-grow overflow-y-auto py-3 px-3"
          role="none"
        >
          <div className="self-stretch px-6 pt-2 flex-col justify-start items-center gap-2 flex">
            <div className="self-stretch h-30 pt-2 opacity-80 flex-col justify-start items-start gap-1 flex">
              <div className="self-stretch h-12 flex-col justify-start items-start gap-2 flex">
                <div className="self-stretch text-fg-brand-subtle label-s-medium">
                  {props.venueDetailsResponse.details.name}
                </div>
                <div className="self-stretch text-fg-neutral-strong label-l-bold">
                  {i18n.get("mapperNote")}
                </div>
              </div>
              <div className="self-stretch text-fg-neutral-strong label-m-medium pt-1">
                {i18n.get("shareYourInsightsToHelp")}
              </div>
            </div>
            <div className="self-stretch rounded-lg justify-start items-center gap-8">
              <div className="mb-2">
                <div className="flex flex-col mt-1">
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <SelectDropDown
                        label={i18n.get("selectNoteStatus")}
                        optionsList={[
                          { id: 0, label: i18n.get("selectStatus") },
                          ...(
                            props.venueDetailsResponse.details
                              .businessNotesActions || []
                          ).map((option) => ({
                            id: option.id,
                            label: option.title,
                          })),
                        ]}
                        value={selectedOption}
                        required={true}
                        onChange={(e) =>
                          handleOptionPress(Number(e.target.value))
                        }
                      />
                    </div>
                    <img
                      src={
                        isDark ? "/images/Info_dark.svg" : "/images/Info.svg"
                      }
                      alt=""
                      aria-hidden="true"
                      className="w-6 h-6 self-start mt-3 cursor-pointer"
                      onClick={props.onInfoClick}
                    />
                  </div>
                </div>

                {/* Area Selection - Only shown when"Incomplete venue" is selected */}
                {selectedOption === 4 && (
                  <>
                    {/* Apply whole venue */}
                    <div className="self-stretch flex flex-col gap-2 pt-3">
                      <h2 className="title-s-semibold text-fg-neutral-strong">
                        {i18n.get("doesTheNoteApplyToTheWholeVenue")}
                      </h2>
                      <div className="flex gap-4">
                        <RadioButton
                          checked={applyToWholeVenue}
                          onCheck={(value) => {
                            if (!applyToWholeVenue && value) {
                              setApplyToWholeVenue(true);
                              setSelectedAreas([]);
                            }
                          }}
                          label="Yes"
                          name="applyToWholeVenue"
                          value="yes"
                        />
                        <RadioButton
                          checked={!applyToWholeVenue}
                          onCheck={(value) => {
                            if (applyToWholeVenue && value) {
                              setApplyToWholeVenue(false);
                            }
                          }}
                          label="No"
                          name="applyToWholeVenue"
                          value="no"
                        />
                      </div>
                    </div>

                    {!applyToWholeVenue && (
                      <div className="mt-4">
                        <MultipleSelectDropDown
                          label={i18n.get("selectNoteLocation")}
                          optionsList={[
                            ...props.venueDetailsResponse.details.businessVenueNotesAreaLocations
                              .filter((area) => area.id !== "Business")
                              .map((area) => ({
                                id: Number(area.id),
                                label: area.name,
                              })),
                          ]}
                          value={selectedAreas}
                          required={true}
                          onChange={(selectedValues) => {
                            setSelectedAreas(selectedValues as number[]);
                          }}
                        />
                      </div>
                    )}
                  </>
                )}

                {/* Conditional Rendering of Return on Date */}
                {selectedOption === 8 && (
                  <div className="my-4">
                    <FloatingLabelInput
                      type="datetime-local"
                      label={i18n.get("returnToVenueOnDate")}
                      value={returnOnDate}
                      onChange={(e) => setReturnOnDate(e.target.value)}
                      min={new Date().toISOString().split("T")[0] + "T00:00"} // Minimum is today's date, starting at 00:00
                      max={
                        new Date(
                          new Date().getTime() + 14 * 24 * 60 * 60 * 1000,
                        )
                          .toISOString()
                          .split("T")[0] + "T23:59" // Maximum is 5 days from today, ending at 23:59
                      }
                      required={true}
                    />
                  </div>
                )}

                {/* Conditional Rendering of Venue part of a client project */}
                {selectedOption === 15 && (
                  <div className="my-4">
                    <FloatingLabelInput
                      type="datetime-local"
                      label={i18n.get("makeVenueVisibleToMappersUntil")}
                      value={actionOnDate}
                      onChange={(e) => setActionOnDate(e.target.value)}
                      min={new Date().toISOString().split("T")[0] + "T00:00"} // Minimum is today's date, starting at 00:00
                      required={false}
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="w-full flex-col justify-center items-start gap-1 flex">
              <div className=" w-full" tabIndex={0}>
                <Textarea
                  id="review"
                  placeholder={i18n.get("addNotesHere")}
                  className="scrollbar-thin h-28"
                  value={notes}
                  aria-describedby="charCount"
                  onChange={(e) => {
                    setNotes(e.target.value);
                  }}
                  maxLength={150}
                  ref={noteRef}
                />
                <div
                  id="charCount"
                  className="label-s-medium text-fg-neutral-subtle mt-3"
                >
                  <span
                    className={
                      isNoteTextMandatory && notes.length < 5
                        ? "text-red-600"
                        : ""
                    }
                  >
                    {notes.length}
                  </span>
                  /150 Characters
                  {isNoteTextMandatory
                    ? " - at least 5 characters required"
                    : ""}
                </div>
              </div>
            </div>
          </div>
        </div>

        <footer
          className={`py-3 min-h-14 flex items-center justify-center text-center px-6 border-border-token-brand-minimal bg-bg-brand-secondary-default ${!isZoomedIn ? "sticky bottom-0 z-10" : ""}`}
        >
          <div className="flex-col justify-end items-center gap-4 flex md:w-full">
            <div className="w-full justify-center items-center inline-flex">
              <WideButton
                disabled={isSubmitDisabled()} // Disable if conditions are not met
                className="grow"
                onClick={() => {
                  submit();
                }}
                aria-label="Submit Mapper note"
              >
                {i18n.get("submit")}
              </WideButton>
            </div>
          </div>
        </footer>
      </section>
    </>
  );
}

export default AddMapperNote;
